import { useQuery } from "@tanstack/react-query"
import { useContext } from 'react'
import { JobAppContext } from '../../GlobalState'
import { getUserStats } from "../../api"

/**
 * Custom hook to fetch user actions/statistics using react-query
 * Uses the current user from JobAppContext
 * 
 * @param {Object} options - Configuration options
 * @param {number} options.limit - Maximum number of records to fetch (default: 100)
 * @param {string} options.action - Filter by action type
 * @param {string} options.startDate - Filter by start date (YYYY-MM-DD)
 * @param {string} options.endDate - Filter by end date (YYYY-MM-DD)
 * @param {string} options.jobSearch - Search jobs by name
 * @param {boolean} options.enabled - Whether the query should be enabled (default: true)
 * @returns {Object} React Query result object with user actions data
 */
export const useUserActions = (options = {}) => {
    const { user, isAuthenticated } = useContext(JobAppContext)
    const { limit = 100, action, startDate, endDate, jobSearch, enabled = true, ...queryOptions } = options

    return useQuery({
        queryKey: ['userStats', user?.id, limit, action, startDate, endDate, jobSearch],
        queryFn: () => getUserStats({ limit, action, startDate, endDate, jobSearch }),
        enabled: enabled && isAuthenticated && !!user?.id,
        // staleTime: 5 * 60 * 1000, // 5 minutes
        // cacheTime: 10 * 60 * 1000, // 10 minutes
        ...queryOptions
    })
}

/**
 * Alternative hook name for semantic clarity
 */
export const useUserStats = useUserActions

export default useUserActions
